// Mercado Pago Payment Link API client
// IMPORTANT: Authorization header uses raw JWT — NO "Bearer " prefix (opposite of other APIs here).

export interface CartItemPayload {
    cart_product_id: number;
    price: number; // 2 decimal places, > 0
    quantity: number; // 1..100
    months: number; // 1..24
    user_id?: number; // commercial agent id — mirrors metadata.seller_id
    classification_id?: number; // ONLY present for Destacado (= 1)
    property_id?: number;
    title?: string;
    description?: string;
}

export interface PayerPayload {
    name: string;
    email: string;
    surname?: string;
    phone_area_code?: string;
    phone_number?: string;
    rfc?: string;
}

export interface InstallmentsPayload {
    enable_msi: boolean;
    max_installments: number;
    default_installments: number;
}

export interface AttributionMetadata {
    deal_id?: number | string;
    seller_id?: number | string;
}

export interface CreatePaymentLinkPayload {
    country: 'MX';
    user_id: number;
    items: CartItemPayload[];
    payer: PayerPayload;
    enable_msi?: boolean;
    max_installments?: number;
    default_installments?: number;
    seller_email?: string;
    metadata?: AttributionMetadata;
    // NEVER include: total, sub_total, detail, source, origin, payment_method,
    // enable_cash, enable_bank_transfer, ttl_hours
}

export interface MercadoPagoSuccessResponse {
    success: true;
    transaction_id: number;
    preference_id: string;
    init_point: string;
    sandbox_init_point: string;
    external_reference: string;
    expires_at: string;
    amount_total: number;
    currency: 'MXN';
    status: 'pending';
}

export interface MercadoPagoErrorResponse {
    success?: false;
    message?: string;
    error?: string;
    mp_status_code?: number;
    errors?: Array<{
        message?: string;
        msg?: string;
        field?: string;
        loc?: (string | number)[];
        path?: string;
    }>;
}

export type MercadoPagoResponse = MercadoPagoSuccessResponse | MercadoPagoErrorResponse;

export const MP_CREATE_PAYMENT_LINK_PATH = 'mercado_pago/create_payment_link';

/**
 * Parses a Mercado Pago API error response into a human-readable string.
 *
 * Shape A (HTTP 400 pydantic): body has errors[] with no success field.
 * Shape B (HTTP 401/422/500/502): body has success:false and a message string.
 */
export function parseMercadoPagoError(status: number, body: unknown): string {
    const b = ((body ?? {}) as Record<string, any>);

    // Shape A: pydantic 400 — has errors[], no success field
    if (Array.isArray(b.errors)) {
        const parts = b.errors
            .map((e: any) => {
                const field = e?.field ?? (Array.isArray(e?.loc) ? e.loc.join('.') : undefined);
                const msg = e?.message ?? e?.msg ?? 'invalid';
                return field ? `${field}: ${msg}` : String(msg);
            })
            .filter(Boolean);
        return parts.length ? parts.join(' · ') : 'Solicitud inválida.';
    }

    // Shape B: success:false with plain message (401/422/500/502)
    if (typeof b.message === 'string' && b.message.length) {
        return typeof b.mp_status_code === 'number'
            ? `${b.message} (MP ${b.mp_status_code})`
            : b.message;
    }

    return `Error ${status}. Intenta nuevamente.`;
}

/**
 * Creates a Mercado Pago payment link.
 *
 * CRITICAL: Authorization header is the RAW JWT token — no "Bearer " prefix.
 * This is intentional and opposite to every other API in this project.
 */
export async function createMercadoPagoPaymentLink(
    payload: CreatePaymentLinkPayload,
    jwt: string,
): Promise<{ status: number; response: MercadoPagoResponse }> {
    const base = process.env.REACT_APP_MP_API_BASE_URL ?? '';
    const url = `${base}${base.endsWith('/') ? '' : '/'}${MP_CREATE_PAYMENT_LINK_PATH}`;

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_MP_API_KEY as string,
            'Authorization': jwt, // RAW JWT — NO 'Bearer ' prefix
        },
        body: JSON.stringify(payload),
    });

    const response = (await res.json()) as MercadoPagoResponse;
    return { status: res.status, response };
}
